import { BoardRenderer } from './render.js';
import { createHistory } from './history.js';
import { exportLegacy, migrateLegacy } from './migrate.js';
import { extractLayers, sortElementsByLegacyTypeOrder } from './layers.js';
import {
  boundsOf,
  hitTestEraser,
  hitTestResizeHandle,
  isResizableElement,
  isSnapTarget,
  pickElementAt,
  pickElementForSelect,
  resizeHandleCursor,
  snapPoint,
  snapResize,
  snapToGrid,
  syncTextBounds,
  TEXT_FONT_FAMILY,
} from './snap.js';
import { uid } from './id.js';
import { containsMath } from './math.js';
import {
  MEDIA_CARD_H,
  MEDIA_CARD_PAD,
  MEDIA_CARD_TITLE_H,
  MEDIA_CARD_W,
  isDomPreviewCard,
  isMediaCardType,
} from './mediaCard.js';
import {
  assignFrameMembership,
  collectFrameDescendants,
  elementsInRect,
  GROUPABLE_TYPES,
  repairAllFrameMembership,
  syncFrameChildren,
} from './grouping.js';
import {
  arrowMatchesEndpoints,
  cardRefId,
  cardRefsEqual,
  endpointFromElement,
  pickLinkEndpoint,
  resolveEndpoint,
  syncRelationArrows,
  updateArrowEndpoints,
} from './linkAnchors.js';

const CARD_W = 240;
const CARD_H = 148;
const IMAGE_CARD_W = MEDIA_CARD_W;
const IMAGE_CARD_H = MEDIA_CARD_H;

function applyCardPayloadFields(el, card) {
  const payload = card.payload || {};
  el.fileMime = payload.mime || null;
  el.fileName = payload.fileName || card.card_name || '';
  el.fileExtension = payload.extension || '';
  el.fileSize = payload.fileSize ?? null;
  const link = payload.link || card.card_link || '';
  if (link) el.pageUrl = link;
  if (payload.title != null && payload.title !== '') el.pageTitle = String(payload.title);
  if (payload.favicon != null && payload.favicon !== '') el.pageFavicon = String(payload.favicon);
  if (payload.embeddable !== undefined) el.embeddable = payload.embeddable;
  if (payload.pageScale != null && payload.pageScale !== '') {
    const scale = Number(payload.pageScale);
    if (Number.isFinite(scale) && scale > 0) el.pageScale = scale;
  }
}

export class Whiteboard {
  constructor(options) {
    this.wrap = options.wrapEl;
    this.canvas = options.canvasEl;
    this.onChange = options.onChange || (() => {});
    this.onInputBroadcast = options.onInputBroadcast || (() => {});
    this.onViewportChange = options.onViewportChange || (() => {});
    this.onLiveChange = options.onLiveChange || (() => {});
    this.replayRemoteInput = false;
    this._remoteMoveSessions = new Map();
    this._collabClientId = null;
    this.onSelectCard = options.onSelectCard || (() => {});
    this.onSelectElement = options.onSelectElement || (() => {});
    this.onCardDblClick = options.onCardDblClick || (() => {});
    this.onHistoryChange = options.onHistoryChange || (() => {});
    this.onLinkPick = options.onLinkPick || (() => {});
    this.onToolChange = options.onToolChange || (() => {});
    this.onFileCardWheel = options.onFileCardWheel || null;
    this.linkPickMode = false;
    this.linkPreview = null;
    this.imageUrls = options.imageUrls || {};

    this.renderer = new BoardRenderer(this.canvas);
    this.renderer.setOnImageLoaded(() => this._scheduleRender());
    this.outlineCanvas = options.outlineCanvasEl || this.wrap?.querySelector?.('#wb-outline-layer') || null;
    this.outlineRenderer = this.outlineCanvas ? new BoardRenderer(this.outlineCanvas) : null;
    if (this.outlineRenderer) {
      this.outlineRenderer._imageCache = this.renderer._imageCache;
    }
    this.history = createHistory();
    this.elements = [];
    this.selectedIds = [];
    this.viewport = { zoom: 100, panX: 0, panY: 0 };
    this.tool = 'select';
    this.readOnly = false;
    this.eraserSize = 20;
    this.eraserPreview = null;
    this._eraserActive = false;
    this.guides = null;
    this.marqueePreview = null;
    this.cards = [];

    this._drag = null;
    this._draw = null;
    this._pointerDrag = false;
    this._spacePan = false;
    this._dirty = false;
    this._raf = null;
    this._textEditEl = null;
    this._frameLabelEditEl = null;

    this._createTextEditor();
    this._createFrameLabelEditor();
    this._bindEvents();
    this._resize();
  }

  setTool(tool) {
    this.tool = tool;
    if (tool !== 'eraser') {
      this.eraserPreview = null;
      this._scheduleRender();
    }
    this._setCanvasCursor('');
    this.wrap.classList.toggle('wb-tool-hand', tool === 'hand');
    this.wrap.classList.toggle('wb-tool-draw', tool === 'draw');
    this.wrap.classList.toggle('wb-tool-eraser', tool === 'eraser');
    this.wrap.classList.toggle('wb-tool-rectangle', tool === 'rectangle');
    this.wrap.classList.toggle('wb-tool-ellipse', tool === 'ellipse');
    this.wrap.classList.toggle('wb-tool-frame', tool === 'frame');
    this.wrap.classList.toggle('wb-tool-arrow', tool === 'arrow');
    this.wrap.classList.toggle('wb-tool-text', tool === 'text');
    this.wrap.classList.toggle('wb-tool-group', tool === 'group');
    this.wrap.classList.toggle('wb-tool-select', tool === 'select');
    if (tool !== 'text') this._finishTextEdit(false);
    this._finishFrameLabelEdit(false);
  }

  setReadOnly(on) {
    this.readOnly = !!on;
    if (this.readOnly) {
      this.setTool('hand');
      this._finishTextEdit(true);
      this._finishFrameLabelEdit(true);
      this.selectedIds = [];
      this.onSelectElement?.(null);
    }
  }

  /** 单次形状工具完成后回到选择（手型、手绘、橡皮、选择保持当前工具） */
  _returnToSelectAfterUse() {
    if (this.replayRemoteInput) return;
    if (
      this.tool === 'hand' ||
      this.tool === 'draw' ||
      this.tool === 'select' ||
      this.tool === 'eraser'
    ) {
      return;
    }
    this.onToolChange('select');
  }

  setLinkPickMode(on) {
    this.linkPickMode = on;
    this.wrap.classList.toggle('wb-link-pick', on);
    if (!on) this.setLinkPreview(null);
  }

  setLinkPreview(preview) {
    this.linkPreview = preview;
    this._scheduleRender();
  }

  getTool() {
    return this.tool;
  }

  getEraserSize() {
    return this.eraserSize;
  }

  isTextEditing() {
    return !!this._textEditEl;
  }

  finishTextEdit(cancel = false) {
    this._finishTextEdit(cancel);
  }

  setEraserSize(px) {
    this.eraserSize = Math.max(8, Math.min(72, Math.round(px)));
    if (this.tool === 'eraser' && this.eraserPreview) {
      this.eraserPreview.r = this.eraserSize;
      this._scheduleRender();
    }
  }

  _eraserRadiusWorld() {
    const zoom = this.viewport.zoom / 100;
    return this.eraserSize / zoom;
  }

  _updateEraserPreview(clientX, clientY) {
    const rect = this.canvas.getBoundingClientRect();
    this.eraserPreview = {
      x: clientX - rect.left,
      y: clientY - rect.top,
      r: this.eraserSize,
    };
  }

  load({ cards, positions, edges, elements, viewport, metadata }) {
    this.cards = cards || [];
    const meta = metadata || {};
    const fromMeta = meta.elements;
    this.elements = migrateLegacy(this.cards, positions || {}, edges || [], fromMeta || elements);
    this.syncCards(this.cards);
    this._layerOrderMigrated = false;
    if (meta.layerOrder !== 'array') {
      this.elements = sortElementsByLegacyTypeOrder(this.elements);
      this._layerOrderMigrated = true;
    }
    this.layerOrder = 'array';
    if (viewport) {
      this.viewport = { ...viewport };
    } else if (meta.viewport) {
      this.viewport = { ...meta.viewport };
    }
    this.selectedIds = [];
    this.history.reset(this._snapshot());
    this.onHistoryChange(this.history.canUndo(), this.history.canRedo());
    repairAllFrameMembership(this.elements);
    syncRelationArrows(this.elements);
    for (const el of this.elements) {
      if (el.type === 'text' && containsMath(el.text)) syncTextBounds(el);
    }
    this._scheduleRender();
  }

  /** 加载时是否做过旧类型分层 → 数组 Z 序迁移（消费后清零） */
  consumeLayerOrderMigration() {
    const v = !!this._layerOrderMigrated;
    this._layerOrderMigrated = false;
    return v;
  }

  setImageUrls(urls) {
    this.imageUrls = urls || {};
    this._scheduleRender();
  }

  preloadImage(fileId, url) {
    if (fileId && url) this.renderer.preloadImage(fileId, url);
  }

  export() {
    const { positions, edges, elements } = exportLegacy(this.elements);
    return {
      positions_json: positions,
      edges_json: edges,
      metadata_json: {
        version: 2,
        layerOrder: 'array',
        viewport: { ...this.viewport },
        elements: elements,
        layers: extractLayers(elements),
      },
    };
  }

  _applyRemoteElementFields(el, remote) {
    for (const key of ['x', 'y', 'width', 'height', 'angle']) {
      if (remote[key] != null) el[key] = remote[key];
    }
    if (Array.isArray(remote.points)) {
      el.points = remote.points.map((p) => (Array.isArray(p) ? [...p] : p));
    }
    if (remote.color != null) el.color = remote.color;
    if (remote.strokeColor != null) el.strokeColor = remote.strokeColor;
    if (remote.fillColor != null) el.fillColor = remote.fillColor;
    if (remote.text != null) el.text = remote.text;
    if (remote.fontSize != null) el.fontSize = remote.fontSize;
    if (remote.label != null) el.label = remote.label;
    if (remote.title != null) el.title = remote.title;
    if (remote.body != null) el.body = remote.body;
    if (remote.cardType != null) el.cardType = remote.cardType;
    if (remote.fileId != null) el.fileId = remote.fileId;
    if (remote.connectionType != null) el.connectionType = remote.connectionType;
    if (remote.connectionLabel != null) el.connectionLabel = remote.connectionLabel;
    if ('frameId' in remote) {
      if (remote.frameId == null) delete el.frameId;
      else el.frameId = remote.frameId;
    }
    if ('groupId' in remote) {
      if (remote.groupId == null) delete el.groupId;
      else el.groupId = remote.groupId;
    }
    if ('sourceFrameId' in remote) {
      if (remote.sourceFrameId == null) delete el.sourceFrameId;
      else el.sourceFrameId = remote.sourceFrameId;
    }
    if ('targetFrameId' in remote) {
      if (remote.targetFrameId == null) delete el.targetFrameId;
      else el.targetFrameId = remote.targetFrameId;
    }
    if ('sourceCardId' in remote) {
      if (remote.sourceCardId == null) delete el.sourceCardId;
      else el.sourceCardId = remote.sourceCardId;
    }
    if ('targetCardId' in remote) {
      if (remote.targetCardId == null) delete el.targetCardId;
      else el.targetCardId = remote.targetCardId;
    }
  }

  setCollabClientId(clientId) {
    this._collabClientId = clientId || null;
  }

  _pointerIdForClient(clientId) {
    const s = String(clientId || 'remote');
    let h = 0;
    for (let i = 0; i < s.length; i += 1) {
      h = (h * 31 + s.charCodeAt(i)) | 0;
    }
    return 1000 + (Math.abs(h) % 9000);
  }

  worldToClient(wx, wy) {
    const screen = this.worldToScreen(wx, wy);
    const rect = this.canvas.getBoundingClientRect();
    return { x: rect.left + screen.x, y: rect.top + screen.y };
  }

  _viewportSnapshot() {
    return {
      zoom: this.viewport.zoom,
      panX: this.viewport.panX,
      panY: this.viewport.panY,
    };
  }

  _applyRemoteViewport(viewport) {
    if (!viewport) return;
    this.viewport.zoom = Math.max(25, Math.min(200, Number(viewport.zoom) || 100));
    this.viewport.panX = Number(viewport.panX) || 0;
    this.viewport.panY = Number(viewport.panY) || 0;
    this._scheduleRender();
  }

  _emitViewportBroadcast() {
    if (this.replayRemoteInput) return;
    const vp = this._viewportSnapshot();
    this.onViewportChange(vp);
    this.onInputBroadcast({
      phase: 'viewport',
      tool: this.tool,
      viewport: vp,
      world: { x: 0, y: 0 },
    });
  }

  _emitBoardInput(phase, world, e = null) {
    if (this.replayRemoteInput) return;
    if (phase !== 'viewport' && !world) return;
    const payload = {
      phase,
      tool: this.tool,
      viewport: this._viewportSnapshot(),
      button: e?.button ?? 0,
      shiftKey: e?.shiftKey ?? false,
      altKey: e?.altKey ?? false,
    };
    if (world) {
      payload.world = { x: world.x, y: world.y };
    }
    this.onInputBroadcast(payload);
  }

  /** 重放协作者指针事件，由本地白板演算结果。 */
  applyRemoteBoardInput(msg) {
    if (!msg || (msg.client_id && msg.client_id === this._collabClientId)) return;

    if (msg.phase === 'viewport') {
      this.replayRemoteInput = true;
      try {
        this._applyRemoteViewport(msg.viewport);
        this.onViewportChange(this._viewportSnapshot());
      } finally {
        this.replayRemoteInput = false;
      }
      return;
    }

    if (!msg.world || msg.world.x == null || msg.world.y == null) return;

    const cid = msg.client_id || 'remote';
    const isSelectDrag =
      msg.tool === 'select' &&
      (msg.phase === 'down' || this._remoteMoveSessions.has(cid));

    if (isSelectDrag) {
      this.replayRemoteInput = true;
      try {
        this._replayRemoteSelectInput(msg);
      } finally {
        this.replayRemoteInput = false;
      }
      return;
    }

    this.replayRemoteInput = true;
    const prevTool = this.tool;
    try {
      if (msg.viewport) this._applyRemoteViewport(msg.viewport);
      if (msg.tool) this.tool = msg.tool;
      const client = this.worldToClient(msg.world.x, msg.world.y);
      const phase = msg.phase || 'move';
      const type =
        phase === 'down' ? 'pointerdown' : phase === 'up' ? 'pointerup' : 'pointermove';
      const buttons = phase === 'down' || phase === 'move' ? 1 : 0;
      const ev = new PointerEvent(type, {
        bubbles: true,
        cancelable: true,
        clientX: client.x,
        clientY: client.y,
        button: msg.button ?? 0,
        buttons,
        pointerId: this._pointerIdForClient(msg.client_id),
        pointerType: 'mouse',
        isPrimary: true,
        shiftKey: msg.shiftKey ?? false,
        altKey: msg.altKey ?? false,
      });
      this.canvas.dispatchEvent(ev);
    } finally {
      this.tool = prevTool;
      this.replayRemoteInput = false;
    }
  }

  _replayRemoteSelectInput(msg) {
    const world = msg.world;
    const cid = msg.client_id || 'remote';
    // Select-drag uses world coords; do not steal the peer's viewport.

    if (msg.phase === 'down') {
      const hit = pickElementAt(this.elements, world.x, world.y);
      if (!hit) {
        this._remoteMoveSessions.delete(cid);
        return;
      }
      this._remoteMoveSessions.set(cid, this._createMoveSession(hit, world));
      if (msg.tool) this.tool = msg.tool;
      this.selectedIds = [hit.id];
      this._scheduleRender();
      return;
    }

    const session = this._remoteMoveSessions.get(cid);
    if (!session) return;

    if (msg.phase === 'move') {
      this._applyMoveSession(session, world);
      return;
    }

    if (msg.phase === 'up') {
      // Geometry already applied; skip history/onChange so peers don't persist remote drags.
      this._finishMoveSession(session, false);
      this._remoteMoveSessions.delete(cid);
    }
  }

  _liveChange(world) {
    if (this.replayRemoteInput) return;
    if (world) this._emitBoardInput('move', world);
    this.onLiveChange();
  }

  /** Resolve card_id / server_id / public_id aliases to the local card row. */
  _findCardByAnyId(cardId) {
    if (cardId == null) return null;
    const key = String(cardId);
    for (const c of this.cards || []) {
      if (String(c.card_id) === key) return c;
      if (c.public_id != null && String(c.public_id) === key) return c;
      if (c.server_id != null && String(c.server_id) === key) return c;
      if (c.local_id != null && String(c.local_id) === key) return c;
      if (c.client_id != null && String(c.client_id) === key) return c;
    }
    return null;
  }

  _lookupPosition(positions_json, cardId) {
    if (!positions_json || cardId == null) return null;
    const direct = positions_json[String(cardId)] ?? positions_json[cardId];
    if (direct && direct.x != null && direct.y != null) return direct;
    const card = this._findCardByAnyId(cardId);
    if (!card) return null;
    for (const key of [card.card_id, card.public_id, card.server_id, card.local_id, card.client_id]) {
      if (key == null) continue;
      const pos = positions_json[String(key)] ?? positions_json[key];
      if (pos && pos.x != null && pos.y != null) return pos;
    }
    return null;
  }

  /** 合并远端布局增量（协作）；不重置撤销栈。 */
  mergeRemoteLayout({ positions_json, edges_json, metadata_json }) {
    const remoteElements = metadata_json?.elements;
    if (Array.isArray(remoteElements)) {
      const localById = new Map(this.elements.map((e) => [e.id, e]));
      const localByCardId = new Map();
      for (const e of this.elements) {
        if (e.type === 'card' && e.cardId != null) {
          localByCardId.set(String(e.cardId), e);
          const card = this._findCardByAnyId(e.cardId);
          if (card?.public_id != null) localByCardId.set(String(card.public_id), e);
          if (card?.server_id != null) localByCardId.set(String(card.server_id), e);
        }
      }
      const merged = [];
      const usedLocalIds = new Set();
      for (const remote of remoteElements) {
        if (!remote?.id && remote?.type !== 'card') continue;
        let local = remote?.id ? localById.get(remote.id) : null;
        if (!local && remote?.type === 'card' && remote.cardId != null) {
          local = localByCardId.get(String(remote.cardId));
          if (!local) {
            const card = this._findCardByAnyId(remote.cardId);
            if (card) local = localByCardId.get(String(card.card_id));
          }
        }
        if (local) {
          this._applyRemoteElementFields(local, remote);
          merged.push(local);
          usedLocalIds.add(local.id);
          continue;
        }
        if (remote?.type === 'card') {
          const card = this._findCardByAnyId(remote.cardId);
          if (!card) continue; // unknown id space — keep local cards, skip orphan remote
          const canonical =
            card.public_id != null && card.public_id !== '' ? card.public_id : card.card_id;
          const clone = JSON.parse(JSON.stringify(remote));
          clone.cardId = canonical;
          clone.id = `card-${canonical}`;
          merged.push(clone);
          usedLocalIds.add(clone.id);
          continue;
        }
        if (!remote?.id) continue;
        merged.push(JSON.parse(JSON.stringify(remote)));
        usedLocalIds.add(remote.id);
      }
      // Keep local cards that remote omitted due to id mismatch (never wipe the board).
      // Insert near original neighbors so orphans are not forced to the visual top.
      const localSnapshot = this.elements.slice();
      for (let localIdx = 0; localIdx < localSnapshot.length; localIdx++) {
        const el = localSnapshot[localIdx];
        if (el.type !== 'card' || usedLocalIds.has(el.id)) continue;
        let insertAt = merged.length;
        for (let i = localIdx - 1; i >= 0; i--) {
          const prev = localSnapshot[i];
          const mi = merged.findIndex((m) => m.id === prev.id);
          if (mi >= 0) {
            insertAt = mi + 1;
            break;
          }
        }
        if (insertAt === merged.length) {
          for (let i = localIdx + 1; i < localSnapshot.length; i++) {
            const next = localSnapshot[i];
            const mi = merged.findIndex((m) => m.id === next.id);
            if (mi >= 0) {
              insertAt = mi;
              break;
            }
          }
        }
        merged.splice(insertAt, 0, el);
        usedLocalIds.add(el.id);
      }
      this.elements = merged;
      syncRelationArrows(this.elements);
    }
    if (positions_json && typeof positions_json === 'object') {
      for (const el of this.elements) {
        if (el.type !== 'card' || !el.cardId) continue;
        const pos = this._lookupPosition(positions_json, el.cardId);
        if (pos && pos.x != null && pos.y != null) {
          el.x = pos.x;
          el.y = pos.y;
        }
      }
    }
    if (
      Array.isArray(edges_json) &&
      edges_json.length &&
      !Array.isArray(remoteElements)
    ) {
      this.elements = migrateLegacy(this.cards, positions_json || {}, edges_json, this.elements);
    }
    this.syncCards(this.cards);
    this._scheduleRender();
  }

  undo() {
    const snap = this.history.undo();
    if (!snap) return false;
    this._applySnapshot(snap);
    return true;
  }

  redo() {
    const snap = this.history.redo();
    if (!snap) return false;
    this._applySnapshot(snap);
    return true;
  }

  syncCards(cards) {
    this.cards = cards || [];
    const byId = new Map();
    const known = new Set();
    for (const c of this.cards) {
      byId.set(String(c.card_id), c);
      known.add(String(c.card_id));
      if (c.public_id != null && c.public_id !== '') {
        byId.set(String(c.public_id), c);
        known.add(String(c.public_id));
      }
      if (c.server_id != null && c.server_id !== '') {
        byId.set(String(c.server_id), c);
        known.add(String(c.server_id));
      }
      if (c.local_id != null) {
        byId.set(String(c.local_id), c);
        known.add(String(c.local_id));
      }
    }
    // Drop orphan card elements left from sync id remaps (numeric vs UUID).
    this.elements = this.elements.filter((el) => {
      if (el.type !== 'card') return true;
      return el.cardId != null && known.has(String(el.cardId));
    });
    for (const el of this.elements) {
      if (el.type !== 'card' || el.cardId == null) continue;
      const c = byId.get(String(el.cardId));
      if (!c) continue;
      // Prefer public_id as canonical canvas identity when present.
      const canonical = c.public_id != null && c.public_id !== '' ? c.public_id : c.card_id;
      if (String(el.cardId) !== String(canonical)) {
        el.cardId = canonical;
        el.id = `card-${canonical}`;
      }
      el.title = c.card_name || '';
      el.body = c.card_content || c.card_notes || '';
      el.cardType = c.card_type || c.payload?.type || 'note';
      el.fileId = c.payload?.fileId ?? c.file_id ?? null;
      el.color = c.payload?.color || null;
      applyCardPayloadFields(el, c);
      if (el.cardType === 'image' && el.fileId && el.width === CARD_W && el.height === CARD_H) {
        el.width = IMAGE_CARD_W;
        el.height = IMAGE_CARD_H;
      }
      if (el.cardType === 'file' && el.fileId && el.width === CARD_W && el.height === CARD_H) {
        el.width = IMAGE_CARD_W;
        el.height = IMAGE_CARD_H;
      }
      if (el.cardType === 'webpage' && el.width === CARD_W && el.height === CARD_H) {
        el.width = IMAGE_CARD_W;
        el.height = IMAGE_CARD_H;
      }
    }
    this._scheduleRender();
  }

  setSelectedStyle(style) {
    if (!this.selectedIds.length) return false;
    for (const id of this.selectedIds) {
      const el = this.elements.find((e) => e.id === id);
      if (!el) continue;
      if (style.strokeColor != null) el.strokeColor = style.strokeColor;
      if (style.fillColor != null && el.type !== 'card') el.fillColor = style.fillColor;
      if (style.color != null && el.type === 'text') {
        el.strokeColor = style.color;
      } else if (style.color != null && el.type === 'card') {
        el.color = style.color;
        if (isMediaCardType(el.cardType)) {
          el.strokeColor = style.color;
        } else {
          el.fillColor = style.color;
        }
      } else if (style.fillColor != null && el.type === 'card' && !isMediaCardType(el.cardType)) {
        el.fillColor = style.fillColor;
      }
    }
    this._commit();
    return true;
  }

  getSelectedElements() {
    return this.elements.filter((el) => this.selectedIds.includes(el.id));
  }

  getSelectedCardIds() {
    const seen = new Set();
    const ids = [];
    for (const el of this.getSelectedElements()) {
      if (el.type !== 'card' || el.cardId == null) continue;
      const id = cardRefId(el.cardId);
      if (!id || seen.has(id)) continue;
      seen.add(id);
      ids.push(id);
    }
    return ids;
  }

  getSelectionScreenCenter() {
    if (!this.selectedIds.length) return null;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const id of this.selectedIds) {
      const el = this.elements.find((e) => e.id === id);
      if (!el) continue;
      const b = boundsOf(el);
      minX = Math.min(minX, b.left);
      minY = Math.min(minY, b.top);
      maxX = Math.max(maxX, b.right);
      maxY = Math.max(maxY, b.bottom);
    }
    if (minX === Infinity) return null;
    const wx = (minX + maxX) / 2;
    const topScreen = this.worldToScreen(minX, minY);
    const rightScreen = this.worldToScreen(maxX, maxY);
    return {
      x: (topScreen.x + rightScreen.x) / 2,
      y: topScreen.y,
    };
  }

  clearSelectedColor() {
    if (!this.selectedIds.length) return false;
    for (const id of this.selectedIds) {
      const el = this.elements.find((e) => e.id === id);
      if (!el) continue;
      if (el.type === 'card') {
        el.color = null;
        el.fillColor = null;
        if (isMediaCardType(el.cardType)) el.strokeColor = null;
      } else if (el.type === 'text') {
        el.strokeColor = null;
      } else {
        el.fillColor = null;
      }
    }
    this._commit();
    return true;
  }

  groupSelected() {
    const els = this.elements.filter(
      (e) => this.selectedIds.includes(e.id) && GROUPABLE_TYPES.has(e.type),
    );
    return this._assignGroupId(els);
  }

  /** 选中元素置于顶层（移到 elements 末尾，保持选中集相对顺序） */
  bringSelectionToFront() {
    if (this.readOnly || !this.selectedIds.length) return false;
    const selected = new Set(this.selectedIds);
    const keep = this.elements.filter((el) => !selected.has(el.id));
    const move = this.elements.filter((el) => selected.has(el.id));
    if (!move.length) return false;
    // 已在顶且相对顺序不变则跳过提交
    const tail = this.elements.slice(-move.length);
    if (
      keep.length + move.length === this.elements.length &&
      tail.every((el, i) => el.id === move[i].id)
    ) {
      return false;
    }
    this.elements = [...keep, ...move];
    this._commit();
    return true;
  }

  /** 选中元素置于底层（移到 elements 开头，保持选中集相对顺序） */
  sendSelectionToBack() {
    if (this.readOnly || !this.selectedIds.length) return false;
    const selected = new Set(this.selectedIds);
    const move = this.elements.filter((el) => selected.has(el.id));
    const keep = this.elements.filter((el) => !selected.has(el.id));
    if (!move.length) return false;
    const head = this.elements.slice(0, move.length);
    if (
      keep.length + move.length === this.elements.length &&
      head.every((el, i) => el.id === move[i].id)
    ) {
      return false;
    }
    this.elements = [...move, ...keep];
    this._commit();
    return true;
  }

  ungroupSelected() {
    let changed = false;
    for (const id of this.selectedIds) {
      const el = this.elements.find((e) => e.id === id);
      if (el?.groupId) {
        delete el.groupId;
        changed = true;
      }
    }
    if (changed) this._commit();
    return changed;
  }

  groupElementsInRect(rx, ry, rw, rh) {
    const els = elementsInRect(this.elements, rx, ry, rw, rh);
    return this._assignGroupId(els);
  }

  _assignGroupId(els) {
    if (els.length < 2) return false;
    const groupId = uid('group');
    for (const el of els) el.groupId = groupId;
    this.selectedIds = els.map((e) => e.id);
    this._notifySelection();
    this._commit();
    return true;
  }

  _notifySelection(clientX = null, clientY = null) {
    const primary = this.elements.find((e) => e.id === this.selectedIds[0]);
    if (!primary) {
      this.onSelectCard(null, clientX, clientY);
      this.onSelectElement(null);
      return;
    }
    if (primary.type === 'card' && primary.cardId) {
      this.onSelectCard(primary.cardId, clientX, clientY);
      this.onSelectElement(null);
    } else {
      this.onSelectCard(null, clientX, clientY);
      this.onSelectElement(primary);
    }
  }

  _getMoveBundle(el) {
    if (this.selectedIds.length > 1 && this.selectedIds.includes(el.id)) {
      return this.elements.filter((e) => this.selectedIds.includes(e.id));
    }
    if (el.groupId) {
      return this.elements.filter((e) => e.groupId === el.groupId);
    }
    if (el.type === 'frame') {
      return [el, ...collectFrameDescendants(el.id, this.elements)];
    }
    return [el];
  }

  addCardElement(card) {
    const i = this.elements.filter((e) => e.type === 'card').length;
    this.addCardElementAt(
      card,
      80 + (i % 4) * 280,
      80 + Math.floor(i / 4) * 180,
    );
  }

  addCardElementAt(card, x, y, options = {}) {
    const cardType = card.card_type || card.payload?.type || 'note';
    const isMedia = isMediaCardType(cardType);
    const canonical =
      card.public_id != null && card.public_id !== ''
        ? card.public_id
        : card.card_id;
    const el = {
      id: `card-${canonical}`,
      type: 'card',
      cardId: canonical,
      x: snapToGrid(x),
      y: snapToGrid(y),
      width: options.width || (isMedia ? IMAGE_CARD_W : CARD_W),
      height: options.height || (isMedia ? IMAGE_CARD_H : CARD_H),
      title: card.card_name || '',
      body: card.card_content || card.card_notes || '',
      cardType,
      fileId: card.payload?.fileId ?? card.file_id ?? null,
      color: card.payload?.color || null,
      fileMime: null,
      fileName: '',
      fileExtension: '',
      fileSize: null,
      pageUrl: card.payload?.link || card.card_link || '',
      pageTitle: card.payload?.title || '',
      pageFavicon: card.payload?.favicon || '',
      embeddable: card.payload?.embeddable,
      pageScale: Number(card.payload?.pageScale) > 0 ? Number(card.payload.pageScale) : 0.5,
    };
    applyCardPayloadFields(el, card);
    this.elements.push(el);
    assignFrameMembership(el, this.elements);
    this._commit();
    this.selectedIds = [el.id];
    this.onSelectCard(card.card_id, null, null);
    this._scheduleRender();
    return el;
  }

  getViewportCenterWorld() {
    const zoom = this.viewport.zoom / 100;
    return {
      x: (this.wrap.clientWidth / 2 - this.viewport.panX) / zoom,
      y: (this.wrap.clientHeight / 2 - this.viewport.panY) / zoom,
    };
  }

  screenToWorld(clientX, clientY) {
    const rect = this.canvas.getBoundingClientRect();
    const zoom = this.viewport.zoom / 100;
    const cx = clientX - rect.left;
    const cy = clientY - rect.top;
    return {
      x: (cx - this.viewport.panX) / zoom,
      y: (cy - this.viewport.panY) / zoom,
    };
  }

  worldToScreen(wx, wy) {
    const zoom = this.viewport.zoom / 100;
    return {
      x: wx * zoom + this.viewport.panX,
      y: wy * zoom + this.viewport.panY,
    };
  }

  findElement(elementId) {
    return this.elements.find((e) => e.id === elementId);
  }

  addCardEdge(sourceId, targetId, connectionType) {
    return this.addRelationEdge(
      { type: 'card', id: cardRefId(sourceId) },
      { type: 'card', id: cardRefId(targetId) },
      connectionType,
    );
  }

  addRelationEdge(source, target, connectionType, style = {}) {
    const srcNorm =
      source?.type === 'card'
        ? { type: 'card', id: cardRefId(source.id) }
        : source;
    const tgtNorm =
      target?.type === 'card'
        ? { type: 'card', id: cardRefId(target.id) }
        : target;
    if (!srcNorm || !tgtNorm || (srcNorm.type === 'card' && !srcNorm.id) || (tgtNorm.type === 'card' && !tgtNorm.id)) {
      return false;
    }
    const srcEl = resolveEndpoint(srcNorm, this.elements);
    const tgtEl = resolveEndpoint(tgtNorm, this.elements);
    if (!srcEl || !tgtEl) return false;
    const dup = this.elements.some(
      (e) => e.type === 'arrow' && arrowMatchesEndpoints(e, srcNorm, tgtNorm),
    );
    if (dup) return false;
    const arrow = {
      id: uid('edge'),
      type: 'arrow',
      points: [
        [0, 0],
        [0, 0],
      ],
      connectionType: connectionType || 'related_to',
      endArrowhead: 'arrow',
      startArrowhead: null,
    };
    if (style.label != null) arrow.connectionLabel = String(style.label).trim();
    if (style.color) arrow.strokeColor = style.color;
    if (srcNorm.type === 'card') arrow.sourceCardId = srcNorm.id;
    else arrow.sourceFrameId = srcNorm.id;
    if (tgtNorm.type === 'card') arrow.targetCardId = tgtNorm.id;
    else arrow.targetFrameId = tgtNorm.id;
    updateArrowEndpoints(arrow, this.elements);
    this.elements.push(arrow);
    this._commit();
    return true;
  }

  removeCardElement(cardId) {
    const cid = cardRefId(cardId);
    this.elements = this.elements.filter(
      (el) => !(el.type === 'card' && cardRefsEqual(el.cardId, cid)),
    );
    this.elements = this.elements.filter(
      (el) =>
        el.type !== 'arrow' ||
        (!cardRefsEqual(el.sourceCardId, cid) && !cardRefsEqual(el.targetCardId, cid)),
    );
    this._commit();
    this._scheduleRender();
  }

  _snapshot() {
    return {
      elements: JSON.parse(JSON.stringify(this.elements)),
      viewport: { ...this.viewport },
    };
  }

  _applySnapshot(snap) {
    this.elements = JSON.parse(JSON.stringify(snap.elements));
    this.viewport = { ...snap.viewport };
    this._scheduleRender();
    this.onChange();
  }

  _commit() {
    this.history.commit(this._snapshot());
    this.onHistoryChange(this.history.canUndo(), this.history.canRedo());
    this.onChange();
    this._scheduleRender();
  }

  _worldFromClient(clientX, clientY) {
    const rect = this.canvas.getBoundingClientRect();
    const zoom = this.viewport.zoom / 100;
    return {
      x: (clientX - rect.left - this.viewport.panX) / zoom,
      y: (clientY - rect.top - this.viewport.panY) / zoom,
    };
  }

  _scheduleRender() {
    if (this._raf) return;
    this._raf = requestAnimationFrame(() => {
      this._raf = null;
      const size = { width: this.wrap.clientWidth, height: this.wrap.clientHeight };
      const state = {
        size,
        elements: this.elements,
        selectedIds: this.selectedIds,
        viewport: this.viewport,
        guides: this.guides,
        eraserPreview: this.eraserPreview,
        imageUrls: this.imageUrls,
        linkPreview: this.linkPreview,
        editingTextId: this._textEditEl?.id,
        editingFrameId: this._frameLabelEditEl?.id,
        marqueePreview: this.marqueePreview,
        outlineSeparate: Boolean(this.outlineRenderer),
      };
      this.renderer.render(state);
      if (this.outlineRenderer) {
        this.outlineRenderer.renderOutline(state);
      }
      this._repositionTextEditor();
      this._repositionFrameLabelEditor();
    });
  }

  _resize() {
    this._resizeFrozen = false;
    const ro = new ResizeObserver(() => {
      if (this._resizeFrozen) return;
      const w = this.wrap.clientWidth;
      const h = this.wrap.clientHeight;
      this.renderer.resize(w, h);
      this.outlineRenderer?.resize(w, h);
      this._scheduleRender();
    });
    ro.observe(this.wrap);
    const w = this.wrap.clientWidth;
    const h = this.wrap.clientHeight;
    this.renderer.resize(w, h);
    this.outlineRenderer?.resize(w, h);
  }

  /** 标签页捕获期间冻结尺寸，避免 Chrome「正在共享」顶栏触发整板缩放抖动 */
  freezeResize() {
    this._resizeFrozen = true;
  }

  unfreezeResize() {
    this._resizeFrozen = false;
    if (!this.wrap) return;
    const w = this.wrap.clientWidth;
    const h = this.wrap.clientHeight;
    this.renderer.resize(w, h);
    this.outlineRenderer?.resize(w, h);
    this._scheduleRender();
  }

  elementScreenRect(el) {
    if (!el) return null;
    const local = this.elementLocalRect(el);
    const wrapRect = this.wrap.getBoundingClientRect();
    return {
      left: wrapRect.left + local.left,
      top: wrapRect.top + local.top,
      width: local.width,
      height: local.height,
      centerX: wrapRect.left + local.left + local.width / 2,
      centerY: wrapRect.top + local.top + local.height / 2,
    };
  }

  /** 元素在 wb-wrap 内的像素矩形（与 canvas 视口变换一致） */
  elementLocalRect(el) {
    if (!el) return null;
    const zoom = this.viewport.zoom / 100;
    return {
      left: this.viewport.panX + el.x * zoom,
      top: this.viewport.panY + el.y * zoom,
      width: (el.width || 0) * zoom,
      height: (el.height || 0) * zoom,
    };
  }

  /** 含描边外扩的包围盒在 wrap 内的像素矩形 */
  elementBoundsLocalRect(el, pad = 12) {
    if (!el) return null;
    const b = boundsOf(el);
    const zoom = this.viewport.zoom / 100;
    return {
      left: this.viewport.panX + b.left * zoom - pad,
      top: this.viewport.panY + b.top * zoom - pad,
      width: Math.max(1, (b.right - b.left) * zoom + pad * 2),
      height: Math.max(1, (b.bottom - b.top) * zoom + pad * 2),
      pad,
      world: b,
    };
  }

  /**
   * 将单个元素画到目标 canvas（用于媒体层 elevated chip）。
   * @returns {{ left: number, top: number, width: number, height: number }|null}
   */
  paintElementOnCanvas(targetCanvas, el, { pad = 12 } = {}) {
    if (!targetCanvas || !el) return null;
    let paintPad = pad;
    if (el.type === 'freedraw') {
      paintPad = Math.max(pad, (Number(el.strokeWidth) || 2) * 4 + 4);
    } else if (el.type === 'arrow') {
      paintPad = Math.max(pad, 16);
    }
    const local = this.elementBoundsLocalRect(el, paintPad);
    if (!local) return null;
    const zoom = this.viewport.zoom / 100;
    const dpr = window.devicePixelRatio || 1;
    const renderer = new BoardRenderer(targetCanvas);
    renderer._imageCache = this.renderer._imageCache;
    renderer.dpr = dpr;
    targetCanvas.width = Math.floor(local.width * dpr);
    targetCanvas.height = Math.floor(local.height * dpr);
    targetCanvas.style.width = `${local.width}px`;
    targetCanvas.style.height = `${local.height}px`;
    const ctx = renderer.ctx;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, local.width, local.height);
    ctx.save();
    ctx.translate(-local.left, -local.top);
    ctx.translate(this.viewport.panX, this.viewport.panY);
    ctx.scale(zoom, zoom);
    renderer.drawElement(el, {
      selectedIds: [],
      imageUrls: this.imageUrls,
      exportMode: true,
    });
    ctx.restore();
    return { left: local.left, top: local.top, width: local.width, height: local.height };
  }

  /** elements[] 下标，未找到为 -1 */
  elementOrderIndex(elOrId) {
    const id = typeof elOrId === 'string' ? elOrId : elOrId?.id;
    if (!id) return -1;
    return this.elements.findIndex((e) => e.id === id);
  }

  /** 是否存在下标更小的文件/网页 DOM 预览卡 */
  hasDomPreviewBelow(el) {
    const idx = this.elementOrderIndex(el);
    if (idx <= 0) return false;
    for (let i = 0; i < idx; i += 1) {
      if (isDomPreviewCard(this.elements[i])) return true;
    }
    return false;
  }

  cardScreenRect(cardId) {
    const el = this.elements.find(
      (e) => e.type === 'card' && cardRefsEqual(e.cardId, cardId),
    );
    return this.elementScreenRect(el);
  }

  getEditingTextId() {
    return this._textEditEl?.id || null;
  }

  /** 需在 DOM 层用 KaTeX 渲染的卡片正文与白板文字 */
  getMathOverlayTargets() {
    const editingId = this._textEditEl?.id;
    const targets = [];
    for (const el of this.elements) {
      if (el.type === 'card' && !isMediaCardType(el.cardType)) {
        const body = (el.body || '').trim();
        if (!containsMath(body)) continue;
        targets.push({
          kind: 'card-body',
          element: el,
          cardId: el.cardId,
          text: body,
          fontSize: 12,
          paddingTop: 38,
          paddingX: 12,
          paddingBottom: 10,
        });
      }
      if (el.type === 'text') {
        const text = (el.text || '').trim();
        if (!containsMath(text) || el.id === editingId) continue;
        targets.push({
          kind: 'text',
          element: el,
          elementId: el.id,
          text,
          fontSize: el.fontSize || 20,
          paddingTop: 0,
          paddingX: 2,
          paddingBottom: 0,
        });
      }
    }
    return targets;
  }

  _isElementInView(el) {
    if (!el) return false;
    const b = boundsOf(el);
    if (!Number.isFinite(b.left) || !Number.isFinite(b.right) || !Number.isFinite(b.top) || !Number.isFinite(b.bottom)) {
      return false;
    }
    // 手绘墨迹比点集 AABB 略宽，避免边缘被视口裁掉导致不建 chip
    let pad = 0;
    if (el.type === 'freedraw') {
      pad = Math.max(8, (Number(el.strokeWidth) || 2) * 4);
    } else if (el.type === 'arrow') {
      pad = 8;
    }
    const zoom = this.viewport.zoom / 100;
    const vx0 = -this.viewport.panX / zoom;
    const vy0 = -this.viewport.panY / zoom;
    const vx1 = vx0 + this.wrap.clientWidth / zoom;
    const vy1 = vy0 + this.wrap.clientHeight / zoom;
    return (
      b.right + pad > vx0
      && b.left - pad < vx1
      && b.bottom + pad > vy0
      && b.top - pad < vy1
    );
  }

  /** 需在 DOM 层预览的文件卡片（PDF / 文本 / 音视频等） */
  getFileOverlayTargets() {
    const targets = [];
    for (const el of this.elements) {
      if (el.type !== 'card' || el.cardType !== 'file' || !el.fileId) continue;
      if (!this._isElementInView(el)) continue;
      const url = this.imageUrls?.[String(el.fileId)];
      if (!url) continue;
      targets.push({
        element: el,
        cardId: el.cardId,
        url,
        contentType: el.fileMime || '',
        fileName: el.fileName || el.title || '',
        extension: el.fileExtension || '',
        sizeBytes: el.fileSize || 0,
        selected: this.selectedIds.includes(el.id),
      });
    }
    return targets;
  }

  /** 需在 DOM 层预览的网页卡片 */
  getWebpageOverlayTargets() {
    const targets = [];
    for (const el of this.elements) {
      if (el.type !== 'card' || el.cardType !== 'webpage') continue;
      const pageUrl = el.pageUrl || '';
      if (!pageUrl) continue;
      if (!this._isElementInView(el)) continue;
      targets.push({
        element: el,
        cardId: el.cardId,
        url: pageUrl,
        title: el.pageTitle || el.title || '',
        favicon: el.pageFavicon || '',
        embeddable: el.embeddable,
        pageScale: Number(el.pageScale) > 0 ? Number(el.pageScale) : 0.5,
        selected: this.selectedIds.includes(el.id),
      });
    }
    return targets;
  }

  centerOnCard(cardId) {
    const el = this.elements.find(
      (e) => e.type === 'card' && cardRefsEqual(e.cardId, cardId),
    );
    if (!el) return;
    const zoom = this.viewport.zoom / 100;
    const cx = el.x + el.width / 2;
    const cy = el.y + el.height / 2;
    this.viewport.panX = this.wrap.clientWidth / 2 - cx * zoom;
    this.viewport.panY = this.wrap.clientHeight / 2 - cy * zoom;
    this.selectedIds = [el.id];
    this._scheduleRender();
    this.onChange();
  }

  _bindEvents() {
    this.canvas.addEventListener('pointerdown', (e) => this._onPointerDown(e));
    this.canvas.addEventListener('dblclick', (e) => this._onDblClick(e));
    this.canvas.addEventListener('wheel', (e) => this._onWheel(e), { passive: false });
    this.canvas.addEventListener('pointermove', (e) => this._onPointerMove(e));
    this.canvas.addEventListener('pointerleave', () => this._setCanvasCursor(''));

    document.addEventListener('keydown', (e) => {
      if (e.code === 'Space' && !e.repeat && !e.target.closest('input, textarea')) {
        this._spacePan = true;
        this.wrap.classList.add('wb-space-pan');
      }
    });
    document.addEventListener('keyup', (e) => {
      if (e.code === 'Space') {
        this._spacePan = false;
        this.wrap.classList.remove('wb-space-pan');
      }
    });
  }

  _setCanvasCursor(cursor) {
    this.canvas.style.cursor = cursor || '';
  }

  _getResizeHandleAt(clientX, clientY) {
    if (this.tool !== 'select' || this._pointerDrag || this._spacePan) return null;
    if (this.selectedIds.length !== 1) return null;
    const el = this.elements.find((e) => e.id === this.selectedIds[0]);
    if (!isResizableElement(el)) return null;
    const zoom = this.viewport.zoom / 100;
    const w = this._worldFromClient(clientX, clientY);
    return hitTestResizeHandle(el, w.x, w.y, zoom);
  }

  _updateHoverCursor(clientX, clientY) {
    if (this.tool !== 'select' || this._pointerDrag || this._spacePan || this.linkPickMode) {
      return;
    }
    const handle = this._getResizeHandleAt(clientX, clientY);
    this._setCanvasCursor(resizeHandleCursor(handle));
  }

  _onPointerMove(e) {
    if (this.tool === 'select') {
      this._updateHoverCursor(e.clientX, e.clientY);
    }
    if (this.tool !== 'eraser' || this._eraserActive) return;
    this._updateEraserPreview(e.clientX, e.clientY);
    this._scheduleRender();
  }

  _onWheel(e) {
    if (!e.ctrlKey && !e.metaKey && this.onFileCardWheel) {
      const w = this._worldFromClient(e.clientX, e.clientY);
      for (const id of this.selectedIds) {
        const el = this.elements.find((item) => item.id === id);
        if (!el || el.type !== 'card' || (el.cardType !== 'file' && el.cardType !== 'webpage')) continue;
        if (w.x < el.x || w.y < el.y || w.x > el.x + el.width || w.y > el.y + el.height) {
          continue;
        }
        if (this.onFileCardWheel(el.id, e.deltaY, e)) {
          e.preventDefault();
          return;
        }
      }
    }
    if (!e.ctrlKey && !e.metaKey) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? -10 : 10;
    const next = Math.max(25, Math.min(200, this.viewport.zoom + delta));
    const rect = this.canvas.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    const s0 = this.viewport.zoom / 100;
    const s1 = next / 100;
    this.viewport.panX = cx - ((cx - this.viewport.panX) * s1) / s0;
    this.viewport.panY = cy - ((cy - this.viewport.panY) * s1) / s0;
    this.viewport.zoom = next;
    this._scheduleRender();
    this._emitViewportBroadcast();
  }

  _onDblClick(e) {
    if (this.readOnly) return;
    if (this.tool !== 'select') return;
    const w = this._worldFromClient(e.clientX, e.clientY);
    const hit = pickElementForSelect(this.elements, w.x, w.y);
    if (hit?.type === 'card' && hit.cardId) {
      if (isMediaCardType(hit.cardType)) {
        this.onCardDblClick(hit.cardId, 'title');
        return;
      }
      const focusField = w.y > hit.y + 36 ? 'body' : 'title';
      this.onCardDblClick(hit.cardId, focusField);
      return;
    }
    if (hit?.type === 'text') {
      this.selectedIds = [hit.id];
      this.onSelectCard(null, e.clientX, e.clientY);
      this.onSelectElement(hit);
      this._openTextEdit(hit);
      return;
    }
    if (hit?.type === 'frame') {
      this.selectedIds = [hit.id];
      this.onSelectCard(null, e.clientX, e.clientY);
      this.onSelectElement(hit);
      this._openFrameLabelEdit(hit);
    }
  }

  _onPointerDown(e) {
    if (this.readOnly) {
      const panTool = true;
      if (panTool) {
        const wPan = this._worldFromClient(e.clientX, e.clientY);
        this._emitBoardInput('down', wPan, e);
        this._startPan(e);
        return;
      }
    }
    if (e.button !== 0 && e.button !== 1) return;
    const panTool =
      this.tool === 'hand' ||
      this._spacePan ||
      e.button === 1 ||
      (e.altKey && this.tool === 'select');
    if (panTool) {
      const wPan = this._worldFromClient(e.clientX, e.clientY);
      this._emitBoardInput('down', wPan, e);
      this._startPan(e);
      return;
    }

    const w = this._worldFromClient(e.clientX, e.clientY);
    this._emitBoardInput('down', w, e);

    if (this.tool === 'draw') {
      this._startFreedraw(e, w);
      return;
    }

    if (this.tool === 'eraser') {
      this._startEraser(e);
      return;
    }

    if (this.tool === 'rectangle') {
      this._startRectangle(e, w);
      return;
    }

    if (this.tool === 'ellipse') {
      this._startEllipse(e, w);
      return;
    }

    if (this.tool === 'frame') {
      this._startFrame(e, w);
      return;
    }

    if (this.tool === 'arrow') {
      this._startArrow(e, w);
      return;
    }

    if (this.tool === 'text') {
      this._finishTextEdit(false);
      this._startText(e, w);
      return;
    }

    if (this.tool === 'group') {
      this._startMarquee(e, w, 'group');
      return;
    }

  // select
    const zoom = this.viewport.zoom / 100;

    if (this.selectedIds.length === 1) {
      const selected = this.elements.find((e) => e.id === this.selectedIds[0]);
      if (selected && isResizableElement(selected)) {
        const handle = hitTestResizeHandle(selected, w.x, w.y, zoom);
        if (handle) {
          this._startResize(e, selected, handle, w);
          this._scheduleRender();
          return;
        }
      }
    }

    const hit = pickElementForSelect(this.elements, w.x, w.y);
    if (hit) {
      if (e.shiftKey) {
        if (this.selectedIds.includes(hit.id)) {
          this.selectedIds = this.selectedIds.filter((id) => id !== hit.id);
        } else {
          this.selectedIds = [...this.selectedIds, hit.id];
        }
        this._notifySelection(e.clientX, e.clientY);
        this._scheduleRender();
        return;
      }
      if (this.linkPickMode) {
        const linkHit = pickLinkEndpoint(
          this.elements,
          w.x,
          w.y,
          pickElementAt,
        );
        const endpoint = endpointFromElement(linkHit);
        if (endpoint) {
          this.selectedIds = [linkHit.id];
          this.onLinkPick(endpoint, e.clientX, e.clientY);
          this._scheduleRender();
          return;
        }
      }
      this.selectedIds = [hit.id];
      this._notifySelection(e.clientX, e.clientY);
      const handle = hitTestResizeHandle(hit, w.x, w.y, zoom);
      if (handle) {
        this._startResize(e, hit, handle, w);
      } else {
        this._startMove(e, hit, w);
      }
    } else {
      this._startMarquee(e, w, 'select');
      return;
    }
    this._updateHoverCursor(e.clientX, e.clientY);
    this._scheduleRender();
  }

  _startPan(e) {
    e.preventDefault();
    const start = { x: e.clientX, y: e.clientY, panX: this.viewport.panX, panY: this.viewport.panY };
    const onMove = (ev) => {
      this.viewport.panX = start.panX + (ev.clientX - start.x);
      this.viewport.panY = start.panY + (ev.clientY - start.y);
      this._scheduleRender();
      const w = this._worldFromClient(ev.clientX, ev.clientY);
      this._liveChange(w);
    };
    const onEnd = (ev) => {
      this.canvas.removeEventListener('pointermove', onMove);
      this.canvas.removeEventListener('pointerup', onEnd);
      const w = ev ? this._worldFromClient(ev.clientX, ev.clientY) : null;
      if (w) this._emitBoardInput('up', w, ev);
      this._emitViewportBroadcast();
    };
    this._safeSetPointerCapture(e.pointerId);
    this.canvas.addEventListener('pointermove', onMove);
    this.canvas.addEventListener('pointerup', onEnd);
  }

  _syncArrowsForElement(el) {
    const ep = endpointFromElement(el);
    if (!ep) return;
    for (const arrow of this.elements) {
      if (arrow.type !== 'arrow') continue;
      const touchesCard =
        ep.type === 'card' &&
        (cardRefsEqual(arrow.sourceCardId, ep.id) ||
          cardRefsEqual(arrow.targetCardId, ep.id));
      const touchesFrame =
        ep.type === 'frame' &&
        (arrow.sourceFrameId === ep.id || arrow.targetFrameId === ep.id);
      if (touchesCard || touchesFrame) {
        updateArrowEndpoints(arrow, this.elements);
      }
    }
  }

  _createMoveSession(el, w) {
    const bundle = this._getMoveBundle(el);
    const bundleIds = new Set(bundle.map((x) => x.id));
    const others = this.elements.filter(
      (x) => !bundleIds.has(x.id) && isSnapTarget(x),
    );
    const startPointer = { x: w.x, y: w.y };
    const snapshots = bundle.map((item) => {
      const b = boundsOf(item);
      return {
        el: item,
        x: item.x ?? b.left,
        y: item.y ?? b.top,
        points: item.points?.map((p) => [...p]),
      };
    });
    const lead = el;
    const leadSnap = snapshots.find((s) => s.el.id === lead.id) || snapshots[0];
    const leadB = boundsOf(lead);
    return {
      startPointer,
      snapshots,
      others,
      lead,
      leadSnap,
      wEl: leadB.right - leadB.left,
      hEl: leadB.bottom - leadB.top,
      hadAlignmentSnap: false,
    };
  }

  _applyMoveSession(session, world) {
    const { startPointer, snapshots, others, lead, leadSnap, wEl, hEl } = session;
    let dx = world.x - startPointer.x;
    let dy = world.y - startPointer.y;
    if (lead.x != null && lead.y != null) {
      const snapped = snapPoint(
        leadSnap.x + dx,
        leadSnap.y + dy,
        others,
        wEl,
        hEl,
        lead.id,
      );
      dx = snapped.x - leadSnap.x;
      dy = snapped.y - leadSnap.y;
      this.guides = snapped.guides;
      session.hadAlignmentSnap = !!(snapped.guides.v?.length || snapped.guides.h?.length);
    } else {
      this.guides = null;
      session.hadAlignmentSnap = false;
    }
    for (const snap of snapshots) {
      const target = snap.el;
      if (snap.points) {
        target.points = snap.points.map((pt) => [
          pt[0] + dx,
          pt[1] + dy,
          ...pt.slice(2),
        ]);
      } else {
        target.x = snap.x + dx;
        target.y = snap.y + dy;
      }
      if (endpointFromElement(target)) {
        this._syncArrowsForElement(target);
      }
    }
    this._scheduleRender();
  }

  _finishMoveSession(session, commitHistory) {
    this.guides = null;
    const skipGrid = session.hadAlignmentSnap;
    for (const snap of session.snapshots) {
      const target = snap.el;
      if (target.x != null && target.y != null) {
        if (!skipGrid) {
          target.x = snapToGrid(target.x);
          target.y = snapToGrid(target.y);
        }
      }
      if (target.type === 'frame') {
        syncFrameChildren(target, this.elements);
        this._syncArrowsForElement(target);
      } else {
        assignFrameMembership(target, this.elements);
      }
    }
    if (commitHistory) this._commit();
  }

  _safeSetPointerCapture(pointerId) {
    try {
      this.canvas.setPointerCapture(pointerId);
    } catch (_) {
      /* 合成 PointerEvent 可能无法 capture */
    }
  }

  _startMove(e, el, w) {
    this._pointerDrag = true;
    const session = this._createMoveSession(el, w);
    const onMove = (ev) => {
      const p = this._worldFromClient(ev.clientX, ev.clientY);
      this._applyMoveSession(session, p);
      this._liveChange(p);
    };
    const onEnd = (ev) => {
      this._pointerDrag = false;
      this.canvas.removeEventListener('pointermove', onMove);
      this.canvas.removeEventListener('pointerup', onEnd);
      this._finishMoveSession(session, true);
      if (ev) {
        const p = this._worldFromClient(ev.clientX, ev.clientY);
        this._emitBoardInput('up', p, ev);
      }
    };
    this._safeSetPointerCapture(e.pointerId);
    this.canvas.addEventListener('pointermove', onMove);
    this.canvas.addEventListener('pointerup', onEnd);
  }

  _startMarquee(e, w, mode) {
    this.marqueePreview = { x: w.x, y: w.y, w: 0, h: 0, mode };
    const start = { x: w.x, y: w.y };
    const onMove = (ev) => {
      const p = this._worldFromClient(ev.clientX, ev.clientY);
      this.marqueePreview = {
        x: Math.min(start.x, p.x),
        y: Math.min(start.y, p.y),
        w: Math.abs(p.x - start.x),
        h: Math.abs(p.y - start.y),
        mode,
      };
      this._scheduleRender();
      this._liveChange(p);
    };
    const onEnd = (ev) => {
      this.canvas.removeEventListener('pointermove', onMove);
      this.canvas.removeEventListener('pointerup', onEnd);
      this.canvas.removeEventListener('pointercancel', onEnd);
      if (ev) {
        const p = this._worldFromClient(ev.clientX, ev.clientY);
        this._emitBoardInput('up', p, ev);
      }
      const m = this.marqueePreview;
      this.marqueePreview = null;
      if (m && m.w >= 8 && m.h >= 8) {
        if (mode === 'group') {
          this.groupElementsInRect(m.x, m.y, m.w, m.h);
          this._returnToSelectAfterUse();
        } else {
          const els = elementsInRect(this.elements, m.x, m.y, m.w, m.h);
          this.selectedIds = els.map((item) => item.id);
          this._notifySelection(ev?.clientX, ev?.clientY);
          this._scheduleRender();
        }
      } else if (mode === 'select') {
        this.selectedIds = [];
        this._notifySelection(ev?.clientX, ev?.clientY);
        this._scheduleRender();
      } else {
        this._scheduleRender();
      }
    };
    this.canvas.setPointerCapture(e.pointerId);
    this.canvas.addEventListener('pointermove', onMove);
    this.canvas.addEventListener('pointerup', onEnd);
    this.canvas.addEventListener('pointercancel', onEnd);
  }

  _minElementSize(el) {
    if (el.type === 'card' && isMediaCardType(el.cardType)) return { w: 120, h: 80 };
    if (el.type === 'card') return { w: 160, h: 100 };
    return { w: 24, h: 24 };
  }

  _startResize(e, el, handle, w) {
    const min = this._minElementSize(el);
    this._pointerDrag = true;
    const cursor = resizeHandleCursor(handle) || 'nwse-resize';
    this.wrap.classList.add('wb-resizing');
    this._setCanvasCursor(cursor);
    this.guides = null;
    let hadAlignmentSnap = false;
    const others = this.elements.filter(
      (x) => x.id !== el.id && isSnapTarget(x),
    );
    const start = {
      x: w.x,
      y: w.y,
      ox: el.x,
      oy: el.y,
      ow: el.width,
      oh: el.height,
      handle,
    };
    const clampMinSize = (nx, ny, nw, nh) => {
      if (nw < min.w) {
        if (handle.includes('w')) nx -= min.w - nw;
        nw = min.w;
      }
      if (nh < min.h) {
        if (handle.includes('n')) ny -= min.h - nh;
        nh = min.h;
      }
      return { nx, ny, nw, nh };
    };
    const onMove = (ev) => {
      const p = this._worldFromClient(ev.clientX, ev.clientY);
      const dx = p.x - start.x;
      const dy = p.y - start.y;
      let nx = start.ox;
      let ny = start.oy;
      let nw = start.ow;
      let nh = start.oh;

      if (handle.includes('e')) nw = start.ow + dx;
      if (handle.includes('w')) {
        nw = start.ow - dx;
        nx = start.ox + dx;
      }
      if (handle.includes('s')) nh = start.oh + dy;
      if (handle.includes('n')) {
        nh = start.oh - dy;
        ny = start.oy + dy;
      }

      ({ nx, ny, nw, nh } = clampMinSize(nx, ny, nw, nh));

      const snapped = snapResize(nx, ny, nw, nh, handle, others, el.id);
      nx = snapped.x;
      ny = snapped.y;
      nw = snapped.width;
      nh = snapped.height;
      ({ nx, ny, nw, nh } = clampMinSize(nx, ny, nw, nh));
      this.guides = snapped.guides;
      hadAlignmentSnap = !!(snapped.guides.v?.length || snapped.guides.h?.length);

      el.x = nx;
      el.y = ny;
      el.width = nw;
      el.height = nh;
      if (endpointFromElement(el)) this._syncArrowsForElement(el);
      this._scheduleRender();
      this._liveChange(p);
    };
    const onEnd = (ev) => {
      if (!hadAlignmentSnap) {
        el.x = snapToGrid(el.x);
        el.y = snapToGrid(el.y);
        el.width = Math.max(min.w, snapToGrid(el.width));
        el.height = Math.max(min.h, snapToGrid(el.height));
      }
      this.guides = null;
      if (el.type === 'frame') {
        syncFrameChildren(el, this.elements);
        this._syncArrowsForElement(el);
      }
      this._pointerDrag = false;
      this.wrap.classList.remove('wb-resizing');
      this.canvas.removeEventListener('pointermove', onMove);
      this.canvas.removeEventListener('pointerup', onEnd);
      if (ev) {
        const p = this._worldFromClient(ev.clientX, ev.clientY);
        this._emitBoardInput('up', p, ev);
      }
      this._commit();
    };
    this.canvas.setPointerCapture(e.pointerId);
    this.canvas.addEventListener('pointermove', onMove);
    this.canvas.addEventListener('pointerup', onEnd);
  }

  static ERASABLE_TYPES = new Set(['freedraw', 'rectangle', 'ellipse', 'arrow', 'text']);

  _createTextEditor() {
    const el = document.createElement('textarea');
    el.className = 'wb-text-editor';
    el.hidden = true;
    el.setAttribute('aria-label', '文字');
    el.rows = 1;
    this.wrap.appendChild(el);
    this.textEditorEl = el;
    el.addEventListener('keydown', (ev) => {
      if (ev.key === 'Escape') {
        ev.preventDefault();
        this._finishTextEdit(true);
      }
    });
    el.addEventListener('blur', () => {
      if (this._textEditEl) this._finishTextEdit(false);
    });
    el.addEventListener('input', () => this._syncTextEditorSize());
  }

  _createFrameLabelEditor() {
    const el = document.createElement('input');
    el.type = 'text';
    el.className = 'wb-frame-label-editor';
    el.hidden = true;
    el.maxLength = 64;
    el.setAttribute('aria-label', '画框名称');
    this.wrap.appendChild(el);
    this.frameLabelEditorEl = el;
    el.addEventListener('keydown', (ev) => {
      if (ev.key === 'Escape') {
        ev.preventDefault();
        this._finishFrameLabelEdit(true);
      } else if (ev.key === 'Enter') {
        ev.preventDefault();
        this._finishFrameLabelEdit(false);
      }
    });
    el.addEventListener('blur', () => {
      if (this._frameLabelEditEl) this._finishFrameLabelEdit(false);
    });
  }

  _repositionTextEditor() {
    const el = this._textEditEl;
    const editor = this.textEditorEl;
    if (!el || !editor || editor.hidden) return;
    const zoom = this.viewport.zoom / 100;
    const fontSize = el.fontSize || 20;
    const screen = this.worldToScreen(el.x, el.y);
    editor.style.left = `${screen.x}px`;
    editor.style.top = `${screen.y}px`;
    editor.style.fontSize = `${fontSize * zoom}px`;
    editor.style.color = el.strokeColor || '#1e1e1e';
    this._syncTextEditorSize();
  }

  _syncTextEditorSize() {
    const editor = this.textEditorEl;
    const el = this._textEditEl;
    if (!editor || !el || editor.hidden) return;
    const zoom = this.viewport.zoom / 100;
    const fontSize = el.fontSize || 20;
    editor.style.height = 'auto';
    editor.style.height = `${Math.max(fontSize * 1.25, editor.scrollHeight)}px`;
    const minW = Math.max(120, (el.width || 160) * zoom);
    editor.style.width = `${Math.max(minW, editor.scrollWidth + 4)}px`;
  }

  _openTextEdit(el) {
    if (!el || el.type !== 'text') return;
    this._finishFrameLabelEdit(false);
    this._textEditEl = el;
    const editor = this.textEditorEl;
    editor.value = el.text || '';
    editor.style.lineHeight = '1.25';
    editor.style.fontFamily = TEXT_FONT_FAMILY.replace(/"/g, '');
    editor.hidden = false;
    this._repositionTextEditor();
    this._scheduleRender();
    requestAnimationFrame(() => {
      editor.focus();
      editor.select();
    });
  }

  _finishTextEdit(cancel) {
    const el = this._textEditEl;
    const editor = this.textEditorEl;
    if (!el || !editor) return;
    editor.hidden = true;
    this._textEditEl = null;
    if (cancel) {
      if (!el.text?.trim()) {
        this.elements = this.elements.filter((x) => x.id !== el.id);
        this.selectedIds = this.selectedIds.filter((id) => id !== el.id);
        this.onSelectElement(null);
      }
    } else {
      const text = editor.value;
      if (!text.trim()) {
        this.elements = this.elements.filter((x) => x.id !== el.id);
        this.selectedIds = this.selectedIds.filter((id) => id !== el.id);
        this.onSelectElement(null);
      } else {
        el.text = text;
        syncTextBounds(el);
        assignFrameMembership(el, this.elements);
        this.selectedIds = [el.id];
        this.onSelectElement(el);
      }
    }
    this._commit();
    if (this.tool === 'text') this._returnToSelectAfterUse();
  }

  _openFrameLabelEdit(frame) {
    if (!frame || frame.type !== 'frame') return;
    this._finishTextEdit(false);
    this._frameLabelEditEl = frame;
    const editor = this.frameLabelEditorEl;
    if (!editor) return;
    editor.value = frame.label || '';
    editor.hidden = false;
    this._repositionFrameLabelEditor();
    this._scheduleRender();
    requestAnimationFrame(() => {
      editor.focus();
      editor.select();
    });
  }

  _repositionFrameLabelEditor() {
    const el = this._frameLabelEditEl;
    const editor = this.frameLabelEditorEl;
    if (!el || !editor || editor.hidden) return;
    const zoom = this.viewport.zoom / 100;
    const screen = this.worldToScreen(el.x + 8, el.y - 18);
    editor.style.left = `${screen.x}px`;
    editor.style.top = `${screen.y}px`;
    editor.style.fontSize = `${Math.max(11, 11 * zoom)}px`;
    editor.style.width = `${Math.max(80, Math.min(el.width || 160, 240) * zoom)}px`;
  }

  _finishFrameLabelEdit(cancel) {
    const el = this._frameLabelEditEl;
    const editor = this.frameLabelEditorEl;
    if (!el || !editor) return;
    editor.hidden = true;
    this._frameLabelEditEl = null;
    if (!cancel) {
      const label = String(editor.value || '').trim().slice(0, 64);
      el.label = label || '分区';
      this.selectedIds = [el.id];
      this.onSelectElement(el);
      this._commit();
    } else {
      this._scheduleRender();
    }
  }

  setFrameLabel(id, label) {
    const el = this.elements.find((e) => e.id === id && e.type === 'frame');
    if (!el) return false;
    el.label = String(label || '').trim().slice(0, 64) || '分区';
    this._commit();
    return true;
  }

  /**
   * 导出选中画框区域 PNG（含框上名称，外扩 padding）。
   * @returns {string|null} data URL
   */
  exportFramePng(frameId, { padding = 28, labelPad = 22, scale = 2, previewBitmaps = null } = {}) {
    const frame = this.elements.find((e) => e.id === frameId && e.type === 'frame');
    if (!frame) return null;
    const kids = collectFrameDescendants(frame.id, this.elements);
    const elements = [frame, ...kids];
    const idSet = new Set(elements.map((e) => e.id));
    for (const el of this.elements) {
      if (el.type !== 'arrow' || idSet.has(el.id)) continue;
      const startId = el.start?.id || el.startId || el.source?.id || el.sourceId;
      const endId = el.end?.id || el.endId || el.target?.id || el.targetId;
      if ((startId && idSet.has(startId)) || (endId && idSet.has(endId))) {
        elements.push(el);
        idSet.add(el.id);
      }
    }

    const bx = frame.x - padding;
    const by = frame.y - labelPad - padding;
    const bw = Math.max(1, frame.width + padding * 2);
    const bh = Math.max(1, frame.height + labelPad + padding * 2);
    const cssW = Math.ceil(bw * scale);
    const cssH = Math.ceil(bh * scale);

    const canvas = document.createElement('canvas');
    const renderer = new BoardRenderer(canvas);
    renderer._imageCache = this.renderer._imageCache;
    renderer.dpr = 1;
    canvas.width = cssW;
    canvas.height = cssH;
    canvas.style.width = `${cssW}px`;
    canvas.style.height = `${cssH}px`;
    renderer.ctx.setTransform(1, 0, 0, 1, 0, 0);

    renderer.render({
      size: { width: cssW, height: cssH },
      viewport: { zoom: 100 * scale, panX: -bx * scale, panY: -by * scale },
      elements,
      selectedIds: [],
      imageUrls: this.imageUrls,
      showGrid: false,
      exportMode: true,
      previewBitmaps: previewBitmaps || {},
    });
    return canvas.toDataURL('image/png');
  }

  _startText(e, w) {
    const el = {
      id: uid('text'),
      type: 'text',
      x: snapToGrid(w.x),
      y: snapToGrid(w.y),
      text: '',
      fontSize: 20,
      width: 160,
      height: 25,
      strokeColor: '#1e1e1e',
    };
    this.elements.push(el);
    this.selectedIds = [el.id];
    this.onSelectCard(null, e.clientX, e.clientY);
    this.onSelectElement(el);
    this._openTextEdit(el);
  }

  _eraseAt(wx, wy, radius) {
    const before = this.elements.length;
    this.elements = this.elements.filter(
      (el) =>
        !Whiteboard.ERASABLE_TYPES.has(el.type) || !hitTestEraser(el, wx, wy, radius),
    );
    return this.elements.length !== before;
  }

  _eraseAlong(x0, y0, x1, y1, radius) {
    const dist = Math.hypot(x1 - x0, y1 - y0);
    if (dist < 0.001) return this._eraseAt(x0, y0, radius);
    const step = Math.max(radius * 0.3, 3);
    const steps = Math.max(1, Math.ceil(dist / step));
    let changed = false;
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      if (this._eraseAt(x0 + (x1 - x0) * t, y0 + (y1 - y0) * t, radius)) {
        changed = true;
      }
    }
    return changed;
  }

  _startEraser(e) {
    e.preventDefault();
    this._eraserActive = true;
    let changed = false;
    let last = null;
    const radius = this._eraserRadiusWorld();

    const applyAt = (wx, wy, clientX, clientY) => {
      let hit = false;
      if (last) {
        if (this._eraseAlong(last.x, last.y, wx, wy, radius)) hit = true;
      } else if (this._eraseAt(wx, wy, radius)) {
        hit = true;
      }
      last = { x: wx, y: wy };
      this._updateEraserPreview(clientX, clientY);
      if (hit) changed = true;
      this._scheduleRender();
      if (hit) this._liveChange();
    };

    const stroke = (ev) => {
      const events = ev.getCoalescedEvents?.() || [ev];
      for (const ce of events) {
        const p = this._worldFromClient(ce.clientX, ce.clientY);
        applyAt(p.x, p.y, ce.clientX, ce.clientY);
      }
    };

    stroke(e);
    const onMove = (ev) => stroke(ev);
    const onEnd = (ev) => {
      this._eraserActive = false;
      this.canvas.removeEventListener('pointermove', onMove);
      this.canvas.removeEventListener('pointerup', onEnd);
      this.canvas.removeEventListener('pointercancel', onEnd);
      if (ev) {
        const p = this._worldFromClient(ev.clientX, ev.clientY);
        this._emitBoardInput('up', p, ev);
      }
      if (changed) this._commit();
      else this._scheduleRender();
    };
    this.canvas.setPointerCapture(e.pointerId);
    this.canvas.addEventListener('pointermove', onMove);
    this.canvas.addEventListener('pointerup', onEnd);
    this.canvas.addEventListener('pointercancel', onEnd);
  }

  _startFreedraw(e, w) {
    const el = {
      id: uid('draw'),
      type: 'freedraw',
      points: [[w.x, w.y, 0.5]],
      strokeColor: null,
      strokeWidth: 2,
    };
    this.elements.push(el);
    const onMove = (ev) => {
      const p = this._worldFromClient(ev.clientX, ev.clientY);
      el.points.push([p.x, p.y, 0.5]);
      this._scheduleRender();
      this._liveChange(p);
    };
    const onEnd = (ev) => {
      this.canvas.removeEventListener('pointermove', onMove);
      this.canvas.removeEventListener('pointerup', onEnd);
      if (ev) {
        const p = this._worldFromClient(ev.clientX, ev.clientY);
        this._emitBoardInput('up', p, ev);
      }
      if (el.points.length < 3) {
        this.elements = this.elements.filter((x) => x.id !== el.id);
      } else {
        assignFrameMembership(el, this.elements);
      }
      this._commit();
    };
    this.canvas.setPointerCapture(e.pointerId);
    this.canvas.addEventListener('pointermove', onMove);
    this.canvas.addEventListener('pointerup', onEnd);
  }

  _startShapeDrag(e, w, type, extra = {}, afterCreate = null) {
    const el = {
      id: uid(type === 'ellipse' ? 'ellipse' : type === 'frame' ? 'frame' : 'rect'),
      type,
      x: w.x,
      y: w.y,
      width: 0,
      height: 0,
      ...extra,
    };
    this.elements.push(el);
    const start = { x: w.x, y: w.y };
    const onMove = (ev) => {
      const p = this._worldFromClient(ev.clientX, ev.clientY);
      el.x = Math.min(start.x, p.x);
      el.y = Math.min(start.y, p.y);
      el.width = Math.abs(p.x - start.x);
      el.height = Math.abs(p.y - start.y);
      this._scheduleRender();
      this._liveChange(p);
    };
    const onEnd = (ev) => {
      this.canvas.removeEventListener('pointermove', onMove);
      this.canvas.removeEventListener('pointerup', onEnd);
      if (ev) {
        const p = this._worldFromClient(ev.clientX, ev.clientY);
        this._emitBoardInput('up', p, ev);
      }
      if (el.width < 8 || el.height < 8) {
        this.elements = this.elements.filter((x) => x.id !== el.id);
      } else {
        el.x = snapToGrid(el.x);
        el.y = snapToGrid(el.y);
        this.selectedIds = [el.id];
        this._notifySelection();
        if (afterCreate) afterCreate(el);
        this._returnToSelectAfterUse();
      }
      this._commit();
    };
    this.canvas.setPointerCapture(e.pointerId);
    this.canvas.addEventListener('pointermove', onMove);
    this.canvas.addEventListener('pointerup', onEnd);
  }

  _startEllipse(e, w) {
    this._startShapeDrag(e, w, 'ellipse');
  }

  _startFrame(e, w) {
    this._startShapeDrag(e, w, 'frame', { label: '分区' }, (frame) => {
      syncFrameChildren(frame, this.elements);
    });
  }

  _startRectangle(e, w) {
    this._startShapeDrag(e, w, 'rectangle');
  }

  _startArrow(e, w) {
    if (!this._arrowDraft) {
      this._arrowDraft = { start: [w.x, w.y], el: null };
      const preview = {
        id: uid('arrow'),
        type: 'arrow',
        points: [
          [w.x, w.y],
          [w.x, w.y],
        ],
      };
      this._arrowDraft.el = preview;
      this.elements.push(preview);
      const onMove = (ev) => {
        const p = this._worldFromClient(ev.clientX, ev.clientY);
        preview.points[1] = [p.x, p.y];
        this._scheduleRender();
        this._liveChange(p);
      };
      const onEnd = (ev) => {
        this.canvas.removeEventListener('pointermove', onMove);
        this.canvas.removeEventListener('pointerup', onEnd);
        const p = this._worldFromClient(ev.clientX, ev.clientY);
        this._emitBoardInput('up', p, ev);
        preview.points[1] = [snapToGrid(p.x), snapToGrid(p.y)];
        const dx = preview.points[1][0] - preview.points[0][0];
        const dy = preview.points[1][1] - preview.points[0][1];
        if (Math.hypot(dx, dy) < 12) {
          this.elements = this.elements.filter((x) => x.id !== preview.id);
        } else {
          this.selectedIds = [preview.id];
          this._notifySelection();
          this._returnToSelectAfterUse();
        }
        this._arrowDraft = null;
        this._commit();
      };
      this.canvas.setPointerCapture(e.pointerId);
      this.canvas.addEventListener('pointermove', onMove);
      this.canvas.addEventListener('pointerup', onEnd);
    }
  }

  removeSelectedElements() {
    if (!this.selectedIds.length) return false;
    const ids = new Set(this.selectedIds);
    const removedCardIds = new Set();
    for (const el of this.elements) {
      if (ids.has(el.id) && el.type === 'card' && el.cardId != null) {
        const cid = cardRefId(el.cardId);
        if (cid) removedCardIds.add(cid);
      }
      if (el.frameId && ids.has(el.frameId)) delete el.frameId;
    }
    this.elements = this.elements.filter((el) => {
      if (ids.has(el.id)) return false;
      if (el.type === 'arrow') {
        if (el.sourceFrameId && ids.has(el.sourceFrameId)) return false;
        if (el.targetFrameId && ids.has(el.targetFrameId)) return false;
        const srcCard = cardRefId(el.sourceCardId);
        const tgtCard = cardRefId(el.targetCardId);
        if (srcCard && removedCardIds.has(srcCard)) return false;
        if (tgtCard && removedCardIds.has(tgtCard)) return false;
      }
      return true;
    });
    this.selectedIds = [];
    this.onSelectCard(null, null, null);
    this.onSelectElement(null);
    this._commit();
    this._scheduleRender();
    return true;
  }

  zoomIn() {
    this.viewport.zoom = Math.min(200, this.viewport.zoom + 10);
    this._scheduleRender();
    this._emitViewportBroadcast();
  }

  zoomOut() {
    this.viewport.zoom = Math.max(25, this.viewport.zoom - 10);
    this._scheduleRender();
    this._emitViewportBroadcast();
  }

  resetView() {
    this.viewport = { zoom: 100, panX: 0, panY: 0 };
    this._scheduleRender();
    this._emitViewportBroadcast();
  }

  setZoom(zoom) {
    this.viewport.zoom = Math.max(25, Math.min(200, zoom));
    this._scheduleRender();
    this._emitViewportBroadcast();
  }

  getViewport() {
    return { ...this.viewport };
  }
}
